// ============================================
// Architect Dashboard Page
// ============================================

import { useState, useEffect, useRef, useCallback } from 'react';
import AppShell from '../layout/AppShell';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../lib/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Doughnut, Bar, Line, PolarArea } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, ArcElement,
  PointElement, LineElement, RadialLinearScale,
  Title, Tooltip, Legend, Filler,
);

// ── Animated counter hook ──
function useAnimatedCounter(target: number, duration = 1200) {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number>();

  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const animate = (now: number) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4); // ease-out quartic
      setValue(Math.round(from + (target - from) * eased));
      if (progress < 1) rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration]);

  return value;
}

// ── Stat card with animated counter ──
function AnimatedStatCard({ icon, label, value, suffix, color, delay }: {
  icon: string; label: string; value: number; suffix?: string; color: string; delay: number;
}) {
  const displayed = useAnimatedCounter(value);
  return (
    <div className="glass-card stat-card animate-float-in" style={{ animationDelay: `${delay}ms`, animationFillMode: 'both' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <span className="stat-card__label" style={{ margin: 0 }}>{label}</span>
        <span style={{ fontSize: '22px', filter: `drop-shadow(0 0 8px ${color})` }}>{icon}</span>
      </div>
      <div className="stat-card__value" style={{ color, textShadow: `0 0 24px ${color}55` }}>
        {displayed}{suffix || ''}
      </div>
    </div>
  );
}

// ── Chart wrapper ──
function ChartCard({ title, children, delay, span }: { title: string; children: React.ReactNode; delay: number; span?: number }) {
  return (
    <div
      className="glass-card animate-float-in"
      style={{
        padding: 'var(--space-5)',
        animationDelay: `${delay}ms`,
        animationFillMode: 'both',
        gridColumn: span ? `span ${span}` : undefined,
      }}
    >
      <h3 style={{
        fontSize: 'var(--font-size-md)', fontWeight: 700,
        marginBottom: 'var(--space-4)', color: 'var(--text-primary)',
        display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
      }}>
        {title}
        <span style={{
          display: 'inline-block', height: '2px', width: '24px',
          background: 'linear-gradient(90deg, var(--accent-primary), transparent)',
          borderRadius: '999px'
        }} />
      </h3>
      {children}
    </div>
  );
}

// ── Shared chart tooltip config ──
const tooltipStyle = {
  backgroundColor: 'rgba(0,0,0,0.85)',
  titleColor: '#F0F0F8',
  bodyColor: '#A0A0B8',
  borderColor: 'rgba(220,20,60,0.4)',
  borderWidth: 1,
  cornerRadius: 8,
  padding: 12,
  titleFont: { weight: '700' as const, size: 13 },
  bodyFont: { size: 12 },
};

// ── Palette ──
const PALETTE = [
  '#DC143C', '#FF4060', '#FF6B8A', '#4D96FF', '#00D46A',
  '#FFB300', '#A855F7', '#14B8A6', '#F97316', '#64748B',
];

export default function ArchitectDashboardPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({ totalUsers: 0, totalTeams: 0, activeUsers: 0, inactiveUsers: 0 });
  const [users, setUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Role change modal
  const [roleModal, setRoleModal] = useState<{ userId: string; currentRole: string } | null>(null);
  const [newRole, setNewRole] = useState('');
  const [pwModal, setPwModal] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  
  // Create user modal
  const [createModalState, setCreateModalState] = useState(false);
  const [createUserForm, setCreateUserForm] = useState({ firstName: '', lastName: '', email: '', password: '', role: 'premier_engineer', teamId: '' });
  const [createError, setCreateError] = useState('');

  // Edit user modal
  const [editUserModal, setEditUserModal] = useState<any | null>(null);
  const [editUserForm, setEditUserForm] = useState({ firstName: '', lastName: '', teamId: '' });
  const [editUserError, setEditUserError] = useState('');

  // Create/Edit Team modal
  const [teamModalState, setTeamModalState] = useState<{ mode: 'create' | 'edit'; initialData?: any } | null>(null);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', managerId: '', smeId: '' });
  const [teamError, setTeamError] = useState('');

  // Scroll to top when any modal opens
  useEffect(() => {
    const anyModalOpen = roleModal || pwModal || createModalState || editUserModal || teamModalState;
    if (anyModalOpen) {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }
  }, [roleModal, pwModal, createModalState, editUserModal, teamModalState]);

  // Inline team transfer state
  const [transferMemberId, setTransferMemberId] = useState<string | null>(null);
  const [transferTargetId, setTransferTargetId] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersData, teamsData] = await Promise.all([
        api.get<any>('/users'),
        api.get<any>('/teams'),
      ]);
      const userList = usersData.users || [];
      setUsers(userList);
      
      // Calculate memberCount directly from users array for accuracy across shards
      const enrichedTeams = (teamsData.teams || []).map((t: any) => {
        const teamMembers = userList.filter((u: any) => u.teamId === t.id);
        const sme = userList.find((u: any) => u.smeForTeam === t.id || (u.role === 'sme' && u.teamId === t.id));
        const manager = userList.find((u: any) => u.teamManagerFor === t.id || (u.role === 'team_leader' && u.teamId === t.id));
        
        return {
          ...t,
          memberCount: teamMembers.length,
          smeName: sme ? `${sme.firstName} ${sme.lastName}` : t.smeName,
          managerName: manager ? `${manager.firstName} ${manager.lastName}` : t.managerName,
        };
      });
      setTeams(enrichedTeams);
      
      setStats({
        totalUsers: userList.length,
        activeUsers: userList.filter((u: any) => u.isActive).length,
        inactiveUsers: userList.filter((u: any) => !u.isActive).length,
        totalTeams: enrichedTeams.length,
      });
    } catch { }
    setLoading(false);
  };

  const handleRoleChange = async () => {
    if (!roleModal || !newRole) return;
    try {
      await api.put(`/users/${roleModal.userId}/role`, { role: newRole });
      setRoleModal(null);
      setNewRole('');
      loadData();
    } catch { }
  };

  const handlePwReset = async () => {
    if (!pwModal || !newPw) return;
    try {
      await api.post(`/users/${pwModal}/set-password`, { newPassword: newPw });
      setPwModal(null);
      setNewPw('');
      loadData();
    } catch { }
  };

  const handleDelete = async (userId: string) => {
    if (!confirm('Deactivate this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      loadData();
    } catch { }
  };

  const handleCreateUser = async () => {
    setCreateError('');
    try {
      await api.post('/users', createUserForm);
      setCreateModalState(false);
      setCreateUserForm({ firstName: '', lastName: '', email: '', password: '', role: 'premier_engineer', teamId: '' });
      loadData();
    } catch (err: any) {
      setCreateError(err.message || 'Creation failed');
    }
  };

  const openEditUser = (u: any) => {
    setEditUserForm({ firstName: u.firstName, lastName: u.lastName, teamId: u.teamId || '' });
    setEditUserError('');
    setEditUserModal(u);
  };

  const handleEditUser = async () => {
    setEditUserError('');
    try {
      await api.put(`/users/${editUserModal.id}`, { firstName: editUserForm.firstName, lastName: editUserForm.lastName });
      
      // Update team if changed
      if (editUserForm.teamId !== (editUserModal.teamId || '')) {
        await api.put(`/users/${editUserModal.id}/team`, { teamId: editUserForm.teamId });
      }

      setEditUserModal(null);
      loadData();
    } catch (err: any) {
      setEditUserError(err.message || 'Edit failed');
    }
  };

  const openCreateTeam = () => {
    setTeamForm({ name: '', description: '', managerId: '', smeId: '' });
    setTeamError('');
    setTeamModalState({ mode: 'create' });
  };

  const openEditTeam = (t: any) => {
    setTeamForm({ name: t.name, description: t.description || '', managerId: t.managerId || '', smeId: t.smeId || '' });
    setTeamError('');
    setTeamModalState({ mode: 'edit', initialData: t });
  };

  const handleSaveTeam = async () => {
    setTeamError('');
    try {
      let teamId = teamModalState?.initialData?.id;
      if (teamModalState?.mode === 'create') {
        const res = await api.post<any>('/teams', { name: teamForm.name, description: teamForm.description });
        teamId = res.id;
      } else {
        await api.put(`/teams/${teamId}`, { name: teamForm.name, description: teamForm.description });
      }

      // Update manager if changed
      if (teamForm.managerId !== teamModalState?.initialData?.managerId && teamForm.managerId) {
        await api.put(`/users/${teamForm.managerId}/team-manager-assignment`, { teamId });
      }

      // Update SME if changed
      if (teamForm.smeId !== teamModalState?.initialData?.smeId && teamForm.smeId) {
        await api.put(`/users/${teamForm.smeId}/sme-assignment`, { teamId });
      }

      setTeamModalState(null);
      loadData();
    } catch (err: any) {
      setTeamError(err.message || 'Team save failed');
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamModalState?.initialData?.id) return;
    if (!confirm(`Delete team "${teamModalState.initialData.name}"? All members will be unassigned.`)) return;
    try {
      await api.delete(`/teams/${teamModalState.initialData.id}`);
      setTeamModalState(null);
      loadData();
    } catch (err: any) {
      setTeamError(err.message || 'Delete failed');
    }
  };

  const handleTransferMember = async (userId: string, currentTeamId: string) => {
    setTeamError('');
    try {
      if (!transferTargetId) {
        // Remove from team
        await api.delete(`/teams/${currentTeamId}/members/${userId}`);
      } else if (transferTargetId !== currentTeamId) {
        // Transfer to another team
        await api.put(`/users/${userId}/team`, { teamId: transferTargetId });
      }
      setTransferMemberId(null);
      setTransferTargetId('');
      loadData();
    } catch (err: any) {
      setTeamError(err.message || 'Transfer failed');
    }
  };

  // ── Derived chart data ──
  const roleLabelsMap: Record<string, string> = {
    architect: 'Architect',
    operations_manager: 'Ops Manager',
    team_leader: 'Team Leader',
    queue_manager: 'Queue Manager',
    sme: 'SME',
    premier_engineer: 'Engineer',
  };

  const roleCounts = users.reduce<Record<string, number>>((acc, u) => {
    acc[u.role] = (acc[u.role] || 0) + 1;
    return acc;
  }, {});

  const roleChartData = {
    labels: Object.keys(roleCounts).map(r => roleLabelsMap[r] || r),
    datasets: [{
      data: Object.values(roleCounts),
      backgroundColor: PALETTE.slice(0, Object.keys(roleCounts).length),
      borderColor: 'transparent',
      borderWidth: 0,
      hoverOffset: 10,
    }],
  };

  const teamChartData = {
    labels: teams.map((t: any) => t.name),
    datasets: [{
      label: 'Members',
      data: teams.map((t: any) => t.memberCount || 0),
      backgroundColor: teams.map((_: any, i: number) => `${PALETTE[i % PALETTE.length]}CC`),
      borderColor: teams.map((_: any, i: number) => PALETTE[i % PALETTE.length]),
      borderWidth: 1,
      borderRadius: 6,
      borderSkipped: false,
    }],
  };

  const parseDateSafe = (dStr: any) => {
    if (!dStr) return new Date(NaN);
    if (dStr instanceof Date) return dStr;
    const s = String(dStr);
    return new Date(s.includes('T') ? s : s.replace(' ', 'T') + 'Z');
  };

  // Build a "join timeline" — group users by creation month, padded to 6 months
  const userTimeline = (() => {
    const months: Record<string, number> = {};
    
    // Initialize last 6 months with 0
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[key] = 0;
    }

    users.forEach(u => {
      if (!u.createdAt) return;
      const d = parseDateSafe(u.createdAt);
      if (isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (months[key] !== undefined) {
        months[key]++;
      } else {
        // If it's older than 6 months, we could ignore or it won't be charted if we only show last 6.
        // Let's just track it anyway, then extract last 6.
        months[key] = (months[key] || 0) + 1;
      }
    });

    const sorted = Object.entries(months).sort(([a], [b]) => a.localeCompare(b));
    // Take last 6 months max
    const recent = sorted.slice(-6);
    return {
      labels: recent.map(([k]) => {
        const [y, m] = k.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${monthNames[parseInt(m) - 1]} ${y}`;
      }),
      data: recent.map(([, v]) => v),
    };
  })();

  const timelineChartData = {
    labels: userTimeline.labels,
    datasets: [{
      label: 'New Users',
      data: userTimeline.data,
      borderColor: '#DC143C',
      backgroundColor: 'rgba(220, 20, 60, 0.08)',
      tension: 0.4,
      fill: true,
      pointBackgroundColor: '#FF4060',
      pointBorderColor: '#DC143C',
      pointBorderWidth: 2,
      pointRadius: 5,
      pointHoverRadius: 8,
      pointHoverBorderWidth: 3,
    }],
  };

  // Team with/without manager/sme breakdown
  const teamHealthData = {
    labels: ['Has Leader', 'No Leader', 'Has SME', 'No SME'],
    datasets: [{
      data: [
        teams.filter((t: any) => t.managerId).length,
        teams.filter((t: any) => !t.managerId).length,
        teams.filter((t: any) => t.smeId).length,
        teams.filter((t: any) => !t.smeId).length,
      ],
      backgroundColor: [
        'rgba(0, 212, 106, 0.7)',
        'rgba(255, 61, 61, 0.7)',
        'rgba(77, 150, 255, 0.7)',
        'rgba(255, 179, 0, 0.7)',
      ],
      borderColor: 'transparent',
    }],
  };

  // ── Recent users for the "activity" section ──
  const recentUsers = [...users].sort((a, b) =>
    parseDateSafe(b.createdAt).getTime() - parseDateSafe(a.createdAt).getTime()
  ).slice(0, 5);

  // ────────────────────────────────────
  //  RENDER: Dashboard Tab
  // ────────────────────────────────────
  const renderDashboard = () => (
    <>
      {/* Animated Stat Cards */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-6)' }}>
        <AnimatedStatCard icon="👥" label="Total Users" value={stats.totalUsers} color="#DC143C" delay={0} />
        <AnimatedStatCard icon="✅" label="Active Users" value={stats.activeUsers} color="#00D46A" delay={80} />
        <AnimatedStatCard icon="🏢" label="Teams" value={stats.totalTeams} color="#4D96FF" delay={160} />
        <AnimatedStatCard icon="🔧" label="Engineers" value={roleCounts['premier_engineer'] || 0} color="#FF4060" delay={240} />
      </div>

      {/* Charts row 1: Users by Role + Team Sizes */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <ChartCard title="Users by Role" delay={300}>
          <div style={{ position: 'relative', maxHeight: '280px', height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Object.keys(roleCounts).length > 0 ? (
              <Doughnut
                data={roleChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: true,
                  cutout: '65%',
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#A0A0B8',
                        padding: 16,
                        font: { size: 11, family: 'Inter', weight: 'bold' },
                        usePointStyle: true,
                        pointStyleWidth: 10,
                      },
                    },
                    tooltip: tooltipStyle as any,
                  },
                  animation: {
                    animateRotate: true,
                    animateScale: true,
                    duration: 1600,
                    easing: 'easeOutQuart',
                  },
                }}
              />
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </ChartCard>

        <ChartCard title="Team Sizes" delay={400}>
          <div style={{ position: 'relative', maxHeight: '280px', height: '280px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {teams.length > 0 ? (
              <Bar
              data={teamChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { display: false },
                  tooltip: tooltipStyle as any,
                },
                scales: {
                  x: {
                    grid: { color: 'rgba(139,0,0,0.08)' },
                    ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' } },
                    border: { color: 'rgba(139,0,0,0.2)' },
                  },
                  y: {
                    beginAtZero: true,
                    suggestedMax: 5,
                    grid: { color: 'rgba(139,0,0,0.08)' },
                    ticks: {
                      color: '#A0A0B8',
                      font: { size: 11, family: 'Inter' },
                      stepSize: 1,
                    },
                    border: { color: 'rgba(139,0,0,0.2)' },
                  },
                },
                animation: { duration: 1400, easing: 'easeOutQuart' },
              }}
            />
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </ChartCard>
      </div>

      {/* Charts row 2: User Growth + Team Health */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 'var(--space-5)', marginBottom: 'var(--space-5)' }}>
        <ChartCard title="User Registration Timeline" delay={500}>
          <div style={{ position: 'relative', maxHeight: '260px', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {userTimeline.data.length > 0 ? (
              <Line
              data={timelineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: { display: false },
                  tooltip: tooltipStyle as any,
                },
                scales: {
                  x: {
                    grid: { color: 'rgba(139,0,0,0.06)' },
                    ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' } },
                    border: { color: 'rgba(139,0,0,0.2)' },
                  },
                  y: {
                    beginAtZero: true,
                    suggestedMax: 5,
                    grid: { color: 'rgba(139,0,0,0.06)' },
                    ticks: { color: '#A0A0B8', font: { size: 11, family: 'Inter' }, stepSize: 1 },
                    border: { color: 'rgba(139,0,0,0.2)' },
                  },
                },
                animation: { duration: 1800, easing: 'easeOutQuart' },
              }}
            />
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </ChartCard>

        <ChartCard title="Team Health" delay={600}>
          <div style={{ position: 'relative', maxHeight: '260px', height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {teams.length > 0 ? (
              <PolarArea
              data={teamHealthData}
              options={{
                responsive: true,
                maintainAspectRatio: true,
                plugins: {
                  legend: {
                    position: 'bottom',
                    labels: {
                      color: '#A0A0B8',
                      padding: 12,
                      font: { size: 10, family: 'Inter', weight: 'bold' },
                      usePointStyle: true,
                      pointStyleWidth: 8,
                    },
                  },
                  tooltip: tooltipStyle as any,
                },
                scales: {
                  r: {
                    grid: { color: 'rgba(139,0,0,0.1)' },
                    ticks: { display: false },
                    beginAtZero: true,
                  },
                },
                animation: { animateRotate: true, animateScale: true, duration: 1600 },
              }}
            />
            ) : (
              <div style={{ color: 'var(--text-muted)' }}>No data available</div>
            )}
          </div>
        </ChartCard>
      </div>

      {/* Recent Users + Quick Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-5)' }}>
        <ChartCard title="Recently Added Users" delay={700}>
          <div className="glass-table-wrapper">
            <table className="glass-table">
              <thead><tr>
                <th>Name</th><th>Email</th><th>Role</th><th>Team</th><th>Joined</th>
              </tr></thead>
              <tbody>
                {recentUsers.map((u) => (
                  <tr key={u.id}>
                    <td data-label="Name" style={{ fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                        <div className="glass-avatar glass-avatar--sm">
                          {u.firstName?.[0]}{u.lastName?.[0]}
                        </div>
                        {u.firstName} {u.lastName}
                      </div>
                    </td>
                    <td data-label="Email" style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
                    <td data-label="Role"><span className="glass-badge glass-badge--role">{(roleLabelsMap[u.role] || u.role)}</span></td>
                    <td data-label="Team" style={{ color: 'var(--text-secondary)' }}>{u.teamName || '—'}</td>
                    <td data-label="Joined" style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>
                      {u.createdAt ? parseDateSafe(u.createdAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
                {recentUsers.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 'var(--space-8)' }}>No users yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </ChartCard>

        <ChartCard title="System Overview" delay={800}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* System status indicator */}
            <div style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              background: 'rgba(0, 212, 106, 0.06)',
              border: '1px solid rgba(0, 212, 106, 0.25)',
              borderRadius: 'var(--radius-md)',
            }}>
              <div style={{
                width: '10px', height: '10px', borderRadius: '50%',
                background: '#00D46A',
                boxShadow: '0 0 12px rgba(0, 212, 106, 0.6)',
                animation: 'pulse-glow 2s ease-in-out infinite',
              }} />
              <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: '#00D46A' }}>System Online</span>
            </div>

            {/* Quick role breakdown */}
            {Object.entries(roleCounts).map(([role, count], i) => {
              const pct = stats.totalUsers > 0 ? Math.round((count / stats.totalUsers) * 100) : 0;
              return (
                <div key={role} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}>
                    <span style={{ fontWeight: 600 }}>{roleLabelsMap[role] || role}</span>
                    <span>{count} ({pct}%)</span>
                  </div>
                  <div style={{
                    height: '6px', borderRadius: '999px',
                    background: 'rgba(255,255,255,0.04)', overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%', borderRadius: '999px',
                      width: `${pct}%`,
                      background: `linear-gradient(90deg, ${PALETTE[i % PALETTE.length]}, ${PALETTE[i % PALETTE.length]}88)`,
                      transition: 'width 1.2s ease',
                    }} />
                  </div>
                </div>
              );
            })}

            {/* Unassigned users */}
            <div style={{
              marginTop: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-4)',
              background: 'rgba(255, 179, 0, 0.06)',
              border: '1px solid rgba(255, 179, 0, 0.2)',
              borderRadius: 'var(--radius-md)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)', fontWeight: 600 }}>Unassigned Users</span>
              <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 800, color: '#FFB300' }}>
                {users.filter(u => !u.teamId).length}
              </span>
            </div>
          </div>
        </ChartCard>
      </div>
    </>
  );

  const renderUsersTable = (list: any[]) => (
    <div className="glass-table-wrapper">
      <table className="glass-table">
        <thead><tr>
          <th>Name</th><th>Email</th><th>Role</th><th>Team</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {list.map((u) => (
            <tr key={u.id}>
              <td data-label="Name" style={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</td>
              <td data-label="Email" style={{ color: 'var(--text-secondary)' }}>{u.email}</td>
              <td data-label="Role"><span className="glass-badge glass-badge--open">{u.role.replace(/_/g, ' ')}</span></td>
              <td data-label="Team" style={{ color: 'var(--text-secondary)' }}>{u.teamName || '—'}</td>
              <td data-label="Actions">
                <div className="flex-gap-2">
                  <button className="glass-btn glass-btn--ghost glass-btn--sm"
                    onClick={() => openEditUser(u)}>
                    Edit
                  </button>
                  <button className="glass-btn glass-btn--ghost glass-btn--sm"
                    onClick={() => { setRoleModal({ userId: u.id, currentRole: u.role }); setNewRole(u.role); }}>
                    Role
                  </button>
                  <button className="glass-btn glass-btn--ghost glass-btn--sm" onClick={() => setPwModal(u.id)}>
                    Reset PW
                  </button>
                  <button className="glass-btn glass-btn--ghost glass-btn--sm" style={{ color: 'var(--error)' }}
                    onClick={() => handleDelete(u.id)}>
                    Deactivate
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderUsers = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>User Management</h2>
        <button className="glass-btn glass-btn--primary" onClick={() => setCreateModalState(true)}>+ Create User</button>
      </div>
      {renderUsersTable(users)}
    </div>
  );

  const renderTeams = () => (
    <div className="glass-card" style={{ padding: 'var(--space-5)' }}>
      <div className="flex-between" style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700 }}>Teams</h2>
        <button className="glass-btn glass-btn--primary" onClick={openCreateTeam}>+ Create Team</button>
      </div>
      <div className="grid-cards">
        {teams.map((t: any) => (
          <div key={t.id} className="glass-card glass-card--hover" style={{ padding: 'var(--space-5)', cursor: 'pointer' }} onClick={() => openEditTeam(t)}>
            <div className="flex-between" style={{ marginBottom: 'var(--space-2)' }}>
              <h3 style={{ fontSize: 'var(--font-size-md)', fontWeight: 700 }}>{t.name}</h3>
              <span className="glass-badge glass-badge--neutral">Edit</span>
            </div>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>{t.description}</p>
            <div className="flex-between" style={{ marginBottom: 'var(--space-1)' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                👥 {t.memberCount || 0} members
              </span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                👑 TL: {t.managerName || 'None'}
              </span>
            </div>
            <div className="flex-between">
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
              </span>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                💡 SME: {t.smeName || 'None'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderContent = () => {
    if (loading) return <div className="flex-center" style={{ padding: 'var(--space-16)' }}><div className="spinner spinner--lg" /></div>;
    switch (activeTab) {
      case 'users': return renderUsers();
      case 'teams': return renderTeams();
      default: return renderDashboard();
    }
  };

  return (
    <AppShell role="architect" activeTab={activeTab} onTabChange={setActiveTab}>
      {renderContent()}

      {/* Role Change Modal */}
      {roleModal && (
        <div className="glass-modal-overlay" onClick={() => setRoleModal(null)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">Change Role</span>
              <button className="glass-modal__close" onClick={() => setRoleModal(null)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group">
                <label className="form-label">New Role</label>
                <select className="glass-select" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                  {['architect', 'operations_manager', 'team_leader', 'queue_manager', 'sme', 'premier_engineer'].map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setRoleModal(null)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={handleRoleChange}>Save Role</button>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {pwModal && (
        <div className="glass-modal-overlay" onClick={() => setPwModal(null)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">Reset Password</span>
              <button className="glass-modal__close" onClick={() => setPwModal(null)}>✕</button>
            </div>
            <div className="glass-modal__body">
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input type="password" className="glass-input" value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} placeholder="Min 8 chars, 1 upper, 1 number, 1 special" />
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setPwModal(null)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={handlePwReset}>Reset Password</button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {createModalState && (
        <div className="glass-modal-overlay" onClick={() => setCreateModalState(false)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">Create New User</span>
              <button className="glass-modal__close" onClick={() => setCreateModalState(false)}>✕</button>
            </div>
            <div className="glass-modal__body">
              {createError && <div className="glass-alert glass-alert--error" style={{ marginBottom: '1rem', color: 'var(--error)' }}>{createError}</div>}
              <div className="grid-cards" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="glass-input" value={createUserForm.firstName} onChange={e => setCreateUserForm({ ...createUserForm, firstName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="glass-input" value={createUserForm.lastName} onChange={e => setCreateUserForm({ ...createUserForm, lastName: e.target.value })} />
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 'var(--space-3)' }}>
                <label className="form-label">Email</label>
                <input className="glass-input" type="email" value={createUserForm.email} onChange={e => setCreateUserForm({ ...createUserForm, email: e.target.value })} />
              </div>
              <div className="form-group" style={{ marginTop: 'var(--space-3)' }}>
                <label className="form-label">Password</label>
                <input className="glass-input" type="password" value={createUserForm.password} onChange={e => setCreateUserForm({ ...createUserForm, password: e.target.value })} placeholder="Min 8 chars, 1 upper, 1 number, 1 special" />
              </div>
              <div className="grid-cards" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select className="glass-select" value={createUserForm.role} onChange={e => setCreateUserForm({ ...createUserForm, role: e.target.value })}>
                    {['architect', 'operations_manager', 'team_leader', 'queue_manager', 'sme', 'premier_engineer'].map((r) => (
                      <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Team (Optional)</label>
                  <select className="glass-select" value={createUserForm.teamId} onChange={e => setCreateUserForm({ ...createUserForm, teamId: e.target.value })}>
                    <option value="">None</option>
                    {teams.map((t: any) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setCreateModalState(false)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={handleCreateUser}>Create User</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editUserModal && (
        <div className="glass-modal-overlay" onClick={() => setEditUserModal(null)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">Edit User Info</span>
              <button className="glass-modal__close" onClick={() => setEditUserModal(null)}>✕</button>
            </div>
            <div className="glass-modal__body">
              {editUserError && <div className="glass-alert glass-alert--error" style={{ marginBottom: '1rem', color: 'var(--error)' }}>{editUserError}</div>}
              <div className="grid-cards" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input className="glass-input" value={editUserForm.firstName} onChange={e => setEditUserForm({ ...editUserForm, firstName: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input className="glass-input" value={editUserForm.lastName} onChange={e => setEditUserForm({ ...editUserForm, lastName: e.target.value })} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Team Assignment</label>
                <select className="glass-select" value={editUserForm.teamId} onChange={e => setEditUserForm({ ...editUserForm, teamId: e.target.value })}>
                  <option value="">None</option>
                  {teams.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="glass-modal__footer">
              <button className="glass-btn glass-btn--secondary" onClick={() => setEditUserModal(null)}>Cancel</button>
              <button className="glass-btn glass-btn--primary" onClick={handleEditUser}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Team Modal */}
      {teamModalState && (
        <div className="glass-modal-overlay" onClick={() => setTeamModalState(null)}>
          <div className="glass-modal" onClick={(e) => e.stopPropagation()}>
            <div className="glass-modal__header">
              <span className="glass-modal__title">{teamModalState.mode === 'create' ? 'Create New Team' : 'Edit Team'}</span>
              <button className="glass-modal__close" onClick={() => setTeamModalState(null)}>✕</button>
            </div>
            <div className="glass-modal__body">
              {teamError && <div className="glass-alert glass-alert--error" style={{ marginBottom: '1rem', color: 'var(--error)' }}>{teamError}</div>}
              <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                <label className="form-label">Team Name</label>
                <input className="glass-input" value={teamForm.name} onChange={e => setTeamForm({ ...teamForm, name: e.target.value })} />
              </div>
              <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
                <label className="form-label">Description</label>
                <input className="glass-input" value={teamForm.description} onChange={e => setTeamForm({ ...teamForm, description: e.target.value })} />
              </div>
              <div className="grid-cards" style={{ gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <div className="form-group">
                  <label className="form-label">Team Leader (Manager)</label>
                  <select className="glass-select" value={teamForm.managerId} onChange={e => setTeamForm({ ...teamForm, managerId: e.target.value })}>
                    <option value="">None</option>
                    {users.filter(u => u.role === 'team_leader').map((u: any) => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Subject Matter Expert (SME)</label>
                  <select className="glass-select" value={teamForm.smeId} onChange={e => setTeamForm({ ...teamForm, smeId: e.target.value })}>
                    <option value="">None</option>
                    {users.filter(u => u.role === 'sme').map((u: any) => (
                      <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              {teamModalState.mode === 'edit' && (
                <div style={{ marginTop: 'var(--space-4)' }}>
                  <div className="flex-between" style={{ marginBottom: 'var(--space-2)' }}>
                    <h4 style={{ fontSize: 'var(--font-size-md)', fontWeight: 600 }}>Team Members</h4>
                  </div>
                  <div className="glass-table-wrapper" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    <table className="glass-table" style={{ fontSize: 'var(--font-size-sm)' }}>
                      <thead><tr>
                        <th>Name</th><th>Role</th><th style={{ textAlign: 'right' }}>Action</th>
                      </tr></thead>
                      <tbody>
                        {users.filter(u => u.teamId === teamModalState.initialData?.id).map(u => (
                          <tr key={u.id}>
                            <td style={{ fontWeight: 500, verticalAlign: 'middle' }}>{u.firstName} {u.lastName}</td>
                            <td style={{ verticalAlign: 'middle' }}><span className="glass-badge glass-badge--sm glass-badge--neutral">{u.role.replace(/_/g, ' ')}</span></td>
                            <td style={{ textAlign: 'right', verticalAlign: 'middle' }}>
                              {transferMemberId === u.id ? (
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
                                  <select 
                                    className="glass-select" 
                                    style={{ padding: '2px 8px', height: '28px', fontSize: '12px' }}
                                    value={transferTargetId} 
                                    onChange={(e) => setTransferTargetId(e.target.value)}
                                  >
                                    <option value="">(Remove from team)</option>
                                    {teams.map((t: any) => (
                                      <option key={t.id} value={t.id} disabled={t.id === teamModalState.initialData?.id}>
                                        {t.name}
                                      </option>
                                    ))}
                                  </select>
                                  <button className="glass-btn glass-btn--primary glass-btn--sm" onClick={() => handleTransferMember(u.id, teamModalState.initialData?.id)}>Save</button>
                                  <button className="glass-btn glass-btn--ghost glass-btn--sm" onClick={() => setTransferMemberId(null)}>Cancel</button>
                                </div>
                              ) : (
                                <button className="glass-btn glass-btn--ghost glass-btn--sm" onClick={() => {
                                  setTransferTargetId(teamModalState.initialData?.id);
                                  setTransferMemberId(u.id);
                                }}>Transfer</button>
                              )}
                            </td>
                          </tr>
                        ))}
                        {users.filter(u => u.teamId === teamModalState.initialData?.id).length === 0 && (
                          <tr><td colSpan={3} style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>No members yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            <div className="glass-modal__footer" style={{ justifyContent: 'space-between' }}>
              <div>
                {teamModalState.mode === 'edit' && (
                  <button className="glass-btn glass-btn--ghost" style={{ color: 'var(--error)' }} onClick={handleDeleteTeam}>
                    🗑 Delete Team
                  </button>
                )}
              </div>
              <div className="flex-gap-2">
                <button className="glass-btn glass-btn--secondary" onClick={() => setTeamModalState(null)}>Cancel</button>
                <button className="glass-btn glass-btn--primary" onClick={handleSaveTeam}>{teamModalState.mode === 'create' ? 'Create Team' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
